/** Copyright ©2026 Devexperts LLC.
All rights reserved. Any unauthorized use will constitute an infringement of copyright.
In case of any questions regarding types of use, please contact legal@devexperts.com.
This notice must remain intact.
**/
import { replaceInArray } from '@devexperts/dxcharts-lite/dist/chart/utils/array.utils';
const LS_KEY = 'custom-js-studies';
/**
 * Local storage-based implementation of CustomStudiesProvider
 */
export const createLocalStorageCustomStudiesProvider = () => {
    const getStudiesFromLS = () => {
        try {
            const stored = localStorage.getItem(LS_KEY);
            if (!stored) {
                return [];
            }
            return JSON.parse(stored);
        }
        catch (error) {
            console.warn('Failed to load custom studies from localStorage:', error);
            return [];
        }
    };
    const setStudiesToLS = (studies) => {
        try {
            localStorage.setItem(LS_KEY, JSON.stringify(studies));
        }
        catch (error) {
            console.error('Failed to save custom studies to localStorage:', error);
        }
    };
    return {
        getAllStudies() {
            return Promise.resolve(getStudiesFromLS());
        },
        getStudy(id) {
            const studies = getStudiesFromLS();
            const study = studies.find(s => s.metainfo.id === id);
            return Promise.resolve(study);
        },
        createStudy(studyToAdd) {
            const studies = getStudiesFromLS();
            const exists = studies.some(s => s.metainfo.id === studyToAdd.metainfo.id);
            if (exists) {
                return Promise.reject(new Error(`Study with id "${studyToAdd.metainfo.id}" already exists`));
            }
            studies.push(studyToAdd);
            setStudiesToLS(studies);
            return Promise.resolve();
        },
        updateStudy(study) {
            const studies = getStudiesFromLS();
            const studyToUpdate = studies.find(s => s.metainfo.id === study.metainfo.id);
            if (!studyToUpdate) {
                return Promise.reject(new Error(`Study with id "${study.metainfo.id}" not found`));
            }
            const updatedStudies = replaceInArray(studies, (s) => s.metainfo.id === study.metainfo.id, study);
            setStudiesToLS(updatedStudies);
            return Promise.resolve();
        },
        deleteStudy(id) {
            const studies = getStudiesFromLS();
            const filteredStudies = studies.filter(s => s.metainfo.id !== id);
            if (filteredStudies.length === studies.length) {
                return Promise.reject(new Error(`Study with id "${id}" not found`));
            }
            setStudiesToLS(filteredStudies);
            return Promise.resolve();
        },
    };
};
